import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useReminderSnoozeMutation } from "web_myplann/Api/Reminders/useReminderSnoozeMutation";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	getReminderSnoozeSchema,
	type ReminderSnoozeFormModel,
} from "web_myplann/Reminders/Forms/reminderSnoozeSchema";
import type { Reminder } from "web_myplann/Reminders/Types/ReminderTypes";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";

type UseReminderSnoozeFormProps = {
	reminder: Reminder;
	onSuccess: () => void;
};

export const useReminderSnoozeForm = ({
	reminder,
	onSuccess,
}: UseReminderSnoozeFormProps) => {
	const { t } = useResource();
	const { mutate: snoozeReminder, isPending } = useReminderSnoozeMutation();

	const resolver = useMemo(
		() => zodResolver(getReminderSnoozeSchema(t, reminder.date)),
		[t, reminder.date],
	);

	const form = useForm<ReminderSnoozeFormModel>({
		resolver,
		mode: "onChange",
		defaultValues: { snoozedUntil: null },
	});

	const submit = form.handleSubmit(({ snoozedUntil }) => {
		if (!snoozedUntil) {
			return;
		}

		snoozeReminder(
			{ id: reminder.id, snoozedUntil },
			{
				onSuccess: () => {
					toast.success(
						t(Resources.Reminders.Snooze.toast, {
							date: dateFormatter(snoozedUntil),
						}),
					);
					onSuccess();
				},
			},
		);
	});

	return {
		form,
		submit,
		isSubmitting: isPending,
	};
};
